import { FilterResults, PostFilterBody, screener } from '@api/api/screener';
import { infiniteQueryOptions } from '@tanstack/react-query';
import type { SortingState } from '@tanstack/react-table';
import { wretchRequest } from '@terminal/utils/api-fetch/apiFetch';

import { hasUsableScreenerFilters } from './utils';

export const QUERY_KEY_SCREENER_RESULTS = 'QUERY_KEY_SCREENER_RESULTS';
export const DEFAULT_POST_FILTER_PAGE_SIZE = 15;

type PostFilterBodyInput = Record<string, PostFilterBody[string] | undefined>;

const sanitizePostFilterBody = (filters: PostFilterBodyInput): PostFilterBody =>
  Object.fromEntries(
    Object.entries(filters).filter(([, filter]) => filter !== undefined)
  ) as PostFilterBody;

interface CreatePostFilterQueryKeyProps {
  requestFilters: PostFilterBody;
  sorting?: SortingState;
  pageSize?: number;
  variant: 'raw' | 'with-ticker-data';
  additionalKeyParts?: readonly unknown[];
}

export const createPostFilterQueryKey = ({
  requestFilters,
  sorting = [],
  pageSize = DEFAULT_POST_FILTER_PAGE_SIZE,
  variant,
  additionalKeyParts = [],
}: CreatePostFilterQueryKeyProps) => [
  QUERY_KEY_SCREENER_RESULTS,
  variant,
  requestFilters,
  sorting,
  pageSize,
  ...additionalKeyParts,
];

interface FetchPostFilterPageProps {
  requestFilters: PostFilterBody;
  sorting?: SortingState;
  pageNumber: number;
  pageSize?: number;
  signal?: AbortSignal;
}

export const fetchPostFilterPage = ({
  requestFilters,
  sorting = [],
  pageNumber,
  pageSize = DEFAULT_POST_FILTER_PAGE_SIZE,
  signal,
}: FetchPostFilterPageProps) =>
  wretchRequest<FilterResults>(screener.postFilter.path, {
    method: 'post',
    body: {
      filters: requestFilters,
      page: {
        number: pageNumber,
        size: pageSize,
      },
      ...(sorting.length && {
        sort_by: {
          field: sorting[0].id,
          desc: sorting[0].desc,
        },
      }),
    },
    signal,
  });

interface CreatePostFilterQueryOptionsProps {
  requestFilters: PostFilterBodyInput;
  sorting?: SortingState;
  staleTime: number;
  pageSize?: number;
  enabled?: boolean;
}

export const createPostFilterQueryOptions = ({
  requestFilters,
  sorting = [],
  staleTime,
  pageSize = DEFAULT_POST_FILTER_PAGE_SIZE,
  enabled = true,
}: CreatePostFilterQueryOptionsProps) => {
  const sanitizedRequestFilters = sanitizePostFilterBody(requestFilters);

  return infiniteQueryOptions({
    queryKey: createPostFilterQueryKey({
      requestFilters: sanitizedRequestFilters,
      sorting,
      pageSize,
      variant: 'raw',
    }),
    queryFn: ({ signal, pageParam }) =>
      fetchPostFilterPage({
        requestFilters: sanitizedRequestFilters,
        sorting,
        pageNumber: pageParam,
        pageSize,
        signal,
      }),
    enabled: enabled && hasUsableScreenerFilters(sanitizedRequestFilters),
    initialPageParam: 1,
    staleTime,
    getNextPageParam: lastData => lastData.page.next,
  });
};
